import { c as _c } from "react/compiler-runtime";
import { useCallback, useId, useMemo, useRef } from 'react';
import { Controller, type ControllerProps, type FieldValues, type Path, useFormContext } from 'react-hook-form';
import InlineAlert from '../../inline-alert';
import SchemaEditor, { type SchemaEditorProps } from '../../schema-editor';
import type { ValidationError } from '../../schema-editor/utils/validate-schema';
import type { LabelTooltipProps } from '../internals/form-label';
import FormWrapper from '../internals/form-wrapper';
export type FormSchemaEditorProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  label?: string | React.ReactNode;
  rules?: ControllerProps<TFieldValues>['rules'];
  hint?: string | React.ReactNode;
  className?: string;
  errorPosition?: 'absolute' | 'relative';
  orientation?: 'vertical' | 'horizontal';
  addon?: React.ReactNode;
  tooltip?: LabelTooltipProps;
  disabled?: boolean;
} & Omit<SchemaEditorProps, 'value' | 'onChange' | 'onValidate' | 'isDisabled'>;
const FormSchemaEditor = t0 => {
  const $ = _c(41);
  let addon;
  let className;
  let hint;
  let label;
  let name;
  let propDisabled;
  let rules;
  let schemaEditorProps;
  let t1;
  let t2;
  let tooltip;
  if ($[0] !== t0) {
    ({
      name,
      label,
      rules,
      hint,
      errorPosition: t1,
      orientation: t2,
      addon,
      className,
      tooltip,
      disabled: propDisabled,
      ...schemaEditorProps
    } = t0);
    $[0] = t0;
    $[1] = addon;
    $[2] = className;
    $[3] = hint;
    $[4] = label;
    $[5] = name;
    $[6] = propDisabled;
    $[7] = rules;
    $[8] = schemaEditorProps;
    $[9] = t1;
    $[10] = t2;
    $[11] = tooltip;
  } else {
    addon = $[1];
    className = $[2];
    hint = $[3];
    label = $[4];
    name = $[5];
    propDisabled = $[6];
    rules = $[7];
    schemaEditorProps = $[8];
    t1 = $[9];
    t2 = $[10];
    tooltip = $[11];
  }
  const errorPosition = t1 === undefined ? "relative" : t1;
  const orientation = t2 === undefined ? "vertical" : t2;
  const {
    control,
    trigger
  } = useFormContext();
  const contextId = useId();
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const validationErrorsRef = useRef(t3);
  let t4;
  if ($[13] !== name || $[14] !== trigger) {
    t4 = (errors, value) => {
      validationErrorsRef.current = errors;
      if (!value || value.trim() === "") {
        return;
      }
      trigger(name);
    };
    $[13] = name;
    $[14] = trigger;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleValidate = t4;
  let t5;
  if ($[16] !== rules) {
    t5 = typeof rules?.validate === "object" ? rules.validate : {};
    $[16] = rules;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = value_0 => {
      if (!value_0 || value_0.trim() === "") {
        return true;
      }
      const errors_0 = validationErrorsRef.current;
      if (errors_0.length > 0) {
        return errors_0.map(_temp).join("\n");
      }
      return true;
    };
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5) {
    t7 = {
      ...t5,
      schemaValidation: t6
    };
    $[19] = t5;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== rules || $[22] !== t7) {
    t8 = {
      ...rules,
      validate: t7
    };
    $[21] = rules;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const mergedRules = t8;
  let t9;
  if ($[24] !== addon || $[25] !== className || $[26] !== contextId || $[27] !== errorPosition || $[28] !== handleValidate || $[29] !== hint || $[30] !== label || $[31] !== orientation || $[32] !== propDisabled || $[33] !== schemaEditorProps || $[34] !== tooltip) {
    t9 = t10 => {
      const {
        field,
        fieldState
      } = t10;
      const error = fieldState.error?.message;
      const errorMessages = error ? error.split("\n") : [];
      return <FormWrapper id={contextId} error={error} label={label} errorPosition={errorPosition} size="default" hint={hint} orientation={orientation} fullWidth={true} disabled={propDisabled || field.disabled} addon={addon} tooltip={tooltip} className={className} hideError={true}><SchemaEditor {...schemaEditorProps} value={field.value ?? ""} onChange={field.onChange} onValidate={handleValidate} isDisabled={field.disabled || propDisabled} />{errorMessages.length > 0 && <div className="flex flex-col gap-1">{errorMessages.map(_temp2)}</div>}</FormWrapper>;
    };
    $[24] = addon;
    $[25] = className;
    $[26] = contextId;
    $[27] = errorPosition;
    $[28] = handleValidate;
    $[29] = hint;
    $[30] = label;
    $[31] = orientation;
    $[32] = propDisabled;
    $[33] = schemaEditorProps;
    $[34] = tooltip;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== control || $[37] !== mergedRules || $[38] !== name || $[39] !== t9) {
    t10 = <Controller name={name} control={control} rules={mergedRules} render={t9} />;
    $[36] = control;
    $[37] = mergedRules;
    $[38] = name;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
};
export default FormSchemaEditor;
function _temp(e) {
  return `${e.path?.length > 0 ? `${e.path}: ` : "root: "}${e.message}`;
}
function _temp2(msg) {
  return <InlineAlert variant="error" key={msg}>{msg}</InlineAlert>;
}